<template>
  <side-panel-inner
    :headerTitle="`Edit ${$store.state.documents.docType.singular}`"
    class="p-document-create"
  >
    <template slot="actions">
      <base-button primary="true" @click="onSubmit" iconBefore="pencil"
        >Publish</base-button
      >
    </template>

    <document-meta v-if="formData" :document="formData"></document-meta>

    <hr />

    <form action v-if="!loading">
      <p>Select the fields you wish to update below</p>
      <p>
        <strong>NOTE:</strong> If changes are made to the ‘Attached file’ you
        must add a new version number to the Policy
      </p>
      <accordion
        title="Policy Title"
        :subtitle="formData.current_version.title"
        expandLink="edit"
      >
        <form-field
          full-width
          name="document_title"
          v-model="formData.current_version.title"
          v-validate="{ required: true, min: 5 }"
          data-vv-as="title"
          :error="errors.first('document_title')"
          :max-length="150"
          :show-counter="true"
          required
          @input="handleVersionMetaDataChange"
        ></form-field>
      </accordion>

      <accordion
        title="Categories"
        :subtitle="categoryString"
        expandLink="edit"
      >
        <!-- Choose Primary Category -->
        <form-field
          label="Choose Category"
          required
          name="document_category"
          helper-text="Select primary category"
        >
          <form-multiselect
            :show-valid-tick="true"
            v-model="formData.primary_category"
            placeholder
            :options="filteredPrimaryCategories"
            :multiple="false"
            v-validate="{ required: true }"
            data-vv-as="primary category"
            id="document_category"
            name="document_category"
            :error="errors.first('document_category')"
            label="name"
            track-by="name"
            @input="handleMetaDataChange"
          ></form-multiselect>
        </form-field>

        <!-- Additional Categories -->
        <form-field
          name="document_additional_categories"
          helper-text="Select additional categories"
        >
          <form-multiselect
            :show-valid-tick="true"
            v-model="formData.categories"
            :options="filteredAdditionalCategories"
            placeholder
            :multiple="true"
            label="name"
            track-by="name"
            @input="handleMetaDataChange"
          ></form-multiselect>
        </form-field>
      </accordion>

      <accordion title="Tags" :subtitle="tagString" expandLink="edit">
        <!-- Tags -->
        <form-multiselect
          :show-valid-tick="true"
          v-model="formData.tags"
          :options="tags"
          :multiple="true"
          label="name"
          track-by="name"
          :taggable="true"
          @tag="addTag"
          @input="handleMetaDataChange"
        ></form-multiselect>
      </accordion>

      <accordion
        title="Policy EqIA Number"
        :subtitle="formData.eqia_number"
        expandLink="edit"
        v-if="docType.singular === 'policy'"
      >
        <form-field
          full-width
          name="document_eqia_number"
          v-model="formData.eqia_number"
          v-validate="{ required: true, min: 5 }"
          data-vv-as="eqia number"
          :error="errors.first('document_eqia_number')"
          :max-length="50"
          :show-counter="true"
          required
          @input="handleMetaDataChange"
        ></form-field>
      </accordion>

      <accordion
        title="Ratified Group"
        :subtitle="ratifiedGroup.name || '(not set)'"
        expandLink="edit"
        v-if="docType.singular === 'policy'"
      >
        <!-- Ratified group -->
        <form-field
          label="Ratified Group"
          required
          name="document_ratified_group"
          helper-text="Select ratified group"
          v-if="docType.singular === 'policy'"
        >
          <form-multiselect
            :show-valid-tick="true"
            v-model="ratifiedGroup"
            placeholder
            :options="ratifiedGroupOptions"
            :multiple="false"
            v-validate="{ required: true }"
            data-vv-as="ratified group"
            id="document_ratified_group"
            name="document_ratified_group"
            :error="errors.first('document_ratified_group')"
            label="name"
            track-by="name"
            @input="handleMetaDataChange"
          ></form-multiselect>
        </form-field>
      </accordion>

      <accordion
        title="Attached File"
        :subtitle="fileSubtitle"
        expandLink="edit"
      >
        <tabs>
          <tabs-item :key="0" :active="showUpload" @click="showUpload = true"
            >File Upload</tabs-item
          >
          <tabs-item :key="1" :active="!showUpload" @click="showUpload = false"
            >Web Form</tabs-item
          >
        </tabs>

        <form-field
          label="File upload"
          name="document_file"
          helper-text="Upload your document"
          required
          v-if="showUpload"
        >
          <file-upload
            v-model="formData.current_version.file"
            :fileTypes="documentFileTypes"
            name="document_file"
            :resource="docType.singular"
            @uploaded="onFileUploaded"
            :docType="docType"
            v-validate="{ required: true }"
            data-vv-as="file upload"
            :error="errors.first('document_file')"
            :resetOnLoad="false"
            :bypassDuplicate="false"
          ></file-upload>
        </form-field>

        <form-field
          full-width
          label="Web Form"
          name="external_resource"
          v-model="formData.current_version.external_url"
          v-validate="{ required: true, min: 5 }"
          data-vv-as="title"
          :error="errors.first('external_resource')"
          v-else
        ></form-field>
        <!-- File Upload -->
      </accordion>

      <accordion
        title="Review Date"
        :subtitle="
          formData.current_version.review_date
            ? $dayjs(formData.current_version.review_date).format(
                'DD / MM / YYYY'
              )
            : ''
        "
        expandLink="edit"
      >
        <form-datepicker
          small
          v-model="formData.current_version.review_date"
          name="document_review_date"
          v-validate="{ required: true }"
          data-vv-as="review date"
          id="document_review_date"
          format="DD / MM / YYYY"
          :error="errors.first('document_review_date')"
          @input="handleVersionMetaDataChange"
        ></form-datepicker>
      </accordion>

      <accordion
        title="Responsible Users"
        :subtitle="userString"
        expandLink="edit"
      >
        <div class="row">
          <div class="col-xs-12">
            <form-label small required>Primary</form-label>
            <user-select
              :users="users"
              v-model="formData.primary_user"
              id="document_primary_user"
              name="document_primary_user"
              v-validate="{ required: true }"
              data-vv-as="primary user"
              :error="errors.first('document_primary_user')"
              :permissions="permissions"
              @input="handleMetaDataChange"
            ></user-select>
          </div>
          <div class="col-xs-12">
            <form-label small required>Secondary</form-label>
            <user-select
              :users="users"
              v-model="formData.secondary_user"
              id="document_secondary_user"
              name="document_secondary_user"
              v-validate="{ required: true, is_not: formData.primary_user }"
              data-vv-as="secondary user"
              :error="errors.first('document_secondary_user')"
              :permissions="permissions"
              @input="handleMetaDataChange"
            ></user-select>
          </div>
        </div>
      </accordion>

      <accordion title="Version" :subtitle="versionNumber" expandLink="edit">
        <!-- Major or Minor Version -->
        <form-options
          label="What type of version?"
          v-model="newVersion"
          name="new_version"
          :options="versionOptions"
          data-vv-as="new version"
          v-validate="{ required: newFileId ? true : false }"
          :error="errors.first('new_version')"
          @input="handleVersionDataChange"
        ></form-options>
        <form-field
          v-if="showCustom"
          full-width
          placeholder="Add a custom version number here"
          v-model="newVersionCustom"
          @input="handleVersionDataChange"
        ></form-field>
      </accordion>

      <message type="error" v-if="showValidationMessage || showVersionError">
        <h3>Form Invalid</h3>
        <p>The form is invalid, please check and try again.</p>
        <p v-if="showVersionError">
          You have changed the file or external URL, but have not updated the
          version number.
        </p>

        <ul v-if="errors">
          <li v-for="error in errors.items">{{ error.msg }}</li>
        </ul>
      </message>
    </form>

    <loading v-else></loading>
  </side-panel-inner>
</template>

<script>
import FileUpload from "@/components/FileUpload";
import Accordion from "@/components/Containers/BaseAccordion";
import UserSelect from "@/components/UserSelect";

import ratifiedGroupOptions from "@/data/ratifiedGroupOptions";

import { categoryFilters } from "@/mixins/categoryFilters";

import { mapState } from "vuex";

export default {
  name: "DocumentEdit",

  components: {
    FileUpload,
    Accordion,
    UserSelect,
  },

  data() {
    return {
      loading: true,
      formData: null,
      newVersion: null,
      newVersionCustom: null,
      newFileId: null,
      showValidationMessage: false,
      permissions: ["add_policies"],
      showVersionError: false,
      creating: false,
      showUpload: true,
      originalUrl: null,
      showCustom: false,
      metaDataDirty: false,
      versionMetaDataDirty: false,
      versionDataDirty: false,
    };
  },

  methods: {
    onSubmit() {
      this.loading = true;

      this.$validator.validate().then((result) => {
        if (!result) {
          this.loading = false;
          this.showValidationMessage = true;
        } else {
          /**
           * If the file has changed, we need to check that the version has changed...
           */
          if (
            this.newFileId &&
            this.newFileId !== this.formData.current_version.id &&
            (!this.newVersion ||
              this.newVersion.toFixed(1) ===
                parseInt(this.formData.current_version.version).toFixed(1))
          ) {
            this.loading = false;
            this.showVersionError = true;
            this.showValidationMessage = true;
            return;
          }
          // same for url
          if (this.originalUrl) {
            if (
              this.originalUrl !== this.formData.current_version.external_url &&
              (!this.newVersion ||
                this.newVersion.toFixed(1) ===
                  parseInt(this.formData.current_version.version).toFixed(1))
            ) {
              this.loading = false;
              this.showVersionError = true;
              this.showValidationMessage = true;
              return;
            }
          }
          this.showValidationMessage = false;
          this.updateDocument();
        }
      });
    },

    updateDocument() {
      const vm = this;
      const formData = this.formData;

      this.creating = true;

      const tagIds = [];

      if (formData.tags) {
        formData.tags.forEach((tag) => {
          if (tag.id) {
            tagIds.push(tag.id);
          } else {
            tagIds.push(tag.name);
          }
        });
      }

      const documentData = {
        title: formData.current_version.title,
        description: "Description Text",
        primary_category_id: formData.primary_category.id,
        primary_user_id: formData.primary_user.id,
        secondary_user_id: formData.secondary_user.id,
        tags: tagIds, //we only need ids
        new_version: this.newVersionCustom
          ? this.newVersionCustom
          : this.newVersion,
        newFileId: this.newFileId,
        id: formData.id,
        current_version: formData.current_version,
        eqia_number: formData.eqia_number,
        ratified_group_id: formData.ratified_group_id,
        review_date: formData.current_version.review_date,
      };

      if (formData.categories) {
        documentData.category_ids = formData.categories.map((cat) => cat.id); //we only need ids
      }
      if (this.metaDataDirty || this.versionDataDirty) {
        const skipVersion = (this.versionMetaDataDirty || this.versionDataDirty) ? false : true;
        this.$store
          .dispatch("documents/update", {documentData, skipVersion})

          .then(() => {
            this.loading = false;
            // if (this.versionMetaDataDirty && !this.versionDataDirty) {
            //   console.log("Version Meta Data Update");
            //   // If we have a review date, we need to update the version
            //   const dateArgs = {
            //     policyId: formData.id,
            //     versionId: formData.current_version.id,
            //     payload: {
            //       review_date: formData.current_version.review_date,
            //       title: formData.current_version.title,
            //     },
            //   };
            //   this.$store
            //     .dispatch("documentVersions/update", dateArgs)
            //     .then(() => (this.loading = false))
            //     .catch((err) => {
            //       this.loading = false;
            //     });
            //   }
            setTimeout(() => {
              vm.$store.dispatch("CLOSE_PANEL");
            }, 1000);
          })
          .catch((err) => {
            this.loading = false;
            this.showValidationMessage = true;
          });
      } else if(this.versionMetaDataDirty) {
        const dateArgs = {
            policyId: formData.id,
            versionId: formData.current_version.id,
            payload: {
              review_date: formData.current_version.review_date,
              title: formData.current_version.title,
            },
          };
          this.$store
            .dispatch("documentVersions/update", dateArgs)
            .then(() => (this.loading = false))
            .catch((err) => {
              this.loading = false;
            });
        setTimeout(() => {
          vm.$store.dispatch("CLOSE_PANEL");
        }, 1000);
      }
    },

    onFileUploaded(fileData) {
      this.newFileId = fileData.id;
    },

    async retrieveDocument() {
      const vm = this;
      const documentId = this.$route.params.id;
      let document;
      let version;

      try {
        document = await this.$store.dispatch("documents/read", documentId);
        this.formData = document;


        if (this.$route.params.versionId) {
          const { versionId } = this.$route.params;
          version = await this.$store.dispatch("documents/getVersion", {
            documentId,
            versionId,
          });
          this.formData.current_version = version.data.data.version;
        }

        this.loading = false;
        this.originalUrl = this.formData.current_version.external_url;
        if (this.formData.current_version.file) {
          this.showUpload = true;
          /**
           * Fake the file as uploaded
           * If there is a file
           */
          this.$store.commit(
            "files/setLocalFile",
            this.formData.current_version.file
          );
          this.$store.commit("files/setUploadPercent", 100);
          this.$store.commit(
            "files/setUploadedFile",
            this.formData.current_version.file
          );
        } else {
          this.showUpload = false;
        }
      } catch (err) {
        console.log("TCL: retrieveDocument -> err", err);
      }
    },
    addTag(newTag) {
      const formTag = {
        id: null,
        name: newTag,
      };
      if (!this.formData.tags) {
        this.formData.tags = [];
      }
      this.formData.tags.push(formTag);
      this.$store.dispatch("tags/createDocumentTag", {name: newTag});
    },
    handleMetaDataChange(e) {
      this.metaDataDirty = true;
    },
    handleVersionDataChange(e) {
      this.versionDataDirty = true;
    },
    handleVersionMetaDataChange(e) {
      this.versionMetaDataDirty = true;
    },
  },

  computed: {
    ...mapState("categories", ["categories"]),
    ...mapState({
      users: (state) => state.users.collection,
    }),
    ...mapState({
      tags: (state) => state.tags.collection,
    }),
    fileSubtitle() {
      if (this.formData.current_version.file) {
        return this.formData.current_version.file.name;
      }

      if (this.formData.current_version.external_url) {
        return this.formData.current_version.external_url;
      }

      return "";
    },
    /**
     * Format groups to format for dropdown
     */
    ratifiedGroupOptions() {
      return ratifiedGroupOptions.map((item) => {
        return { ...item, name: item.label };
      });
    },
    /**
     * Get the current ratified group string
     */
    ratifiedGroup: {
      get() {
        const group = this.ratifiedGroupOptions.filter(
          (group) => group.id === this.formData.ratified_group_id
        );

        if (group.length < 1) {
          return {};
        }

        return { id: group[0].id, name: group[0].name };
      },
      set(newVal) {
        this.formData.ratified_group_id = newVal.id;
      },
    },

    /**
     * Current version number in float format
     */
    versionNumber() {
      return parseFloat(this.formData.current_version.version)
        .toPrecision(2)
        .toString()
    },
    /**
     * comma-separated list of categories
     */
    categoryString() {
      let primary = "";
      if (this.formData.primary_category) {
        primary = this.formData.primary_category.name;
      }
      const secondaries = this.formData.categories;

      let returnString = primary;

      if (secondaries) {
        secondaries.forEach((cat) => {
          returnString += `, ${cat.name}`;
        });
      }

      return returnString;
    },
    /**
     * comma-separated list of tags
     */
    tagString() {
      const tags = this.formData.tags;

      if (!tags || tags.length < 1) {
        return "(No tags added)";
      }

      let returnString = tags[0].name;

      tags.forEach((tag, i) => {
        if (i > 0) {
          returnString += `, ${tag.name}`;
        }
      });

      return returnString;
    },
    /**
     * comma-separated list of users
     */
    userString() {
      let primary = "";
      let secondary = "";
      if (this.formData.primary_user) {
        primary = this.formData.primary_user.name
          ? this.formData.primary_user.name
          : this.formData.primary_user.email;
      }
      if (this.formData.secondary_user) {
        secondary = this.formData.secondary_user.name
          ? `, ${this.formData.secondary_user.name}`
          : `, ${this.formData.secondary_user.email}`;
      }

      return `${primary} ${secondary}`;
    },
    versionOptions() {
      const majorVersion = parseInt(this.formData.current_version.version) + 1;
      const minorVersion =
        parseFloat(this.formData.current_version.version) + 0.1;

      return [
        {
          label: `Major (v${majorVersion}.0)`,
          value: majorVersion,
          content: "",
        },
        {
          label: `Minor (v${minorVersion.toFixed(1)})`,
          value: minorVersion,
          content: "",
        },
        {
          label: "Custom",
          value: "custom",
          content: "",
        },
      ];
    },

    documentFileTypes() {
      if (this.docType.singular == "policy") {
        return ["pdf","xls","xlsx"];
      }

      return null;
    },

    docType() {
      return this.$store.state.documents.docType;
    },
  },

  beforeCreate() {
    this.$store.dispatch("categories/list");
    this.$store.dispatch("users/list");
  },

  beforeMount() {
    // so that the background isn't empty...
    if (
      !this.$store.state.documents[this.$store.state.documents.docType.plural]
    ) {
      this.$store.dispatch("documents/list");
    }
  },

  created() {
    this.retrieveDocument();
    this.$store.dispatch("tags/listDocumentTags");
  },
  mixins: [categoryFilters],
  watch: {
    newVersion(newValue, oldValue) {
      this.showCustom = newValue == "custom";
    },
  },
};
</script>
